import { api } from "./api";
import type {
  AuthSession,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  GoogleSignInRequest,
  LoginRequest,
  LogoutResponse,
  MagicLinkRequest,
  MagicLinkRequestResponse,
  MagicLinkVerifyRequest,
  ResendVerificationResponse,
  ResetPasswordRequest,
  SignupRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from "@/types";

// Source: backend/app/api/v1/auth.py + ENG-AUTH-LOGIN-SIGNUP.
//
// All endpoints rely on the shared axios instance's
// ``withCredentials: true`` config so the ``aiaso_session`` cookie
// flows transparently. The session is httpOnly — these helpers do
// NOT and MUST NOT read or write the cookie themselves.

export async function signup(payload: SignupRequest): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>("/api/v1/auth/signup", payload);
  return data;
}

export async function login(payload: LoginRequest): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>("/api/v1/auth/login", payload);
  return data;
}

export async function logout(): Promise<LogoutResponse> {
  const { data } = await api.post<LogoutResponse>("/api/v1/auth/logout");
  return data;
}

export async function fetchCurrentSession(): Promise<AuthSession> {
  const { data } = await api.get<AuthSession>("/api/v1/auth/me");
  return data;
}

export async function forgotPassword(
  payload: ForgotPasswordRequest,
): Promise<ForgotPasswordResponse> {
  const { data } = await api.post<ForgotPasswordResponse>(
    "/api/v1/auth/forgot-password",
    payload,
  );
  return data;
}

export async function resetPassword(
  payload: ResetPasswordRequest,
): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>(
    "/api/v1/auth/reset-password",
    payload,
  );
  return data;
}

export async function verifyEmail(
  payload: VerifyEmailRequest,
): Promise<VerifyEmailResponse> {
  const { data } = await api.post<VerifyEmailResponse>(
    "/api/v1/auth/verify-email",
    payload,
  );
  return data;
}

export async function resendVerification(): Promise<ResendVerificationResponse> {
  const { data } = await api.post<ResendVerificationResponse>(
    "/api/v1/auth/resend-verification",
  );
  return data;
}

export async function requestMagicLink(
  payload: MagicLinkRequest,
): Promise<MagicLinkRequestResponse> {
  const { data } = await api.post<MagicLinkRequestResponse>(
    "/api/v1/auth/magic-link/request",
    payload,
  );
  return data;
}

export async function verifyMagicLink(
  payload: MagicLinkVerifyRequest,
): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>(
    "/api/v1/auth/magic-link/verify",
    payload,
  );
  return data;
}

export async function googleSignIn(
  payload: GoogleSignInRequest,
): Promise<AuthSession> {
  const { data } = await api.post<AuthSession>("/api/v1/auth/google", payload);
  return data;
}
